import { type SessionResponse, sessionResponseSchema } from "@mailhand/core/schemas";
import { type KeyValueStore, parseJson } from "../lib/storage.js";

const STORAGE_KEY = "hooky.session";

export type Session = SessionResponse;

type Listener = () => void;

/** The signed-in member's session, in memory and in the given store. */
export class SessionStore {
  private current: Session | null;
  private readonly listeners = new Set<Listener>();
  private readonly store: KeyValueStore;

  constructor(store: KeyValueStore) {
    this.store = store;
    this.current = readStored(store);
  }

  get = (): Session | null => this.current;

  token = (): string | null => this.current?.session ?? null;

  set(session: Session): void {
    this.current = session;
    this.store.setItem(STORAGE_KEY, JSON.stringify(session));
    this.emit();
  }

  clear(): void {
    if (this.current === null) return;
    this.current = null;
    this.store.removeItem(STORAGE_KEY);
    this.emit();
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

function readStored(store: KeyValueStore): Session | null {
  const raw = store.getItem(STORAGE_KEY);
  if (raw === null) return null;
  const parsed = sessionResponseSchema.safeParse(parseJson(raw));
  return parsed.success ? parsed.data : null;
}
